import React, { forwardRef, useState } from 'react'

import type { DropdownMenuItemProps } from '@radix-ui/react-dropdown-menu'

import { ItemVariants } from 'ui/components/Menu/Menu.css'
import { Spinner } from 'ui/components/Spinner'
import { StandardComponentProps } from 'ui/helpers/styles'

import { Item, LeftSlot, RightSlot } from './Dropdown.parts'
import { useDropdownContext } from './dropdownContext'

export type SlotContentProps = {
    disabled?: boolean
}

type DropdownItemBaseRef = HTMLDivElement

type DropdownItemBaseProps = Omit<DropdownMenuItemProps, 'asChild'> &
    StandardComponentProps &
    ItemVariants & {
        leftSlotContent?: React.FC<SlotContentProps>
        rightSlotContent?: React.FC<SlotContentProps>
        closeOnSelect?: boolean
        isLoading?: boolean
    }

export const DropdownItemBase = forwardRef<DropdownItemBaseRef, DropdownItemBaseProps>(
    (
        {
            leftSlotContent: LeftSlotContent,
            rightSlotContent: RightSlotContent,
            children,
            onSelect,
            closeOnSelect = true,
            isLoading,
            ...props
        },
        ref
    ) => {
        const [isLoadingInternal, setIsLoadingInternal] = useState(false)
        const effectiveIsLoading = isLoading ?? isLoadingInternal

        const { closeRoot } = useDropdownContext()!

        const handleSelect = async (event: Event) => {
            if (effectiveIsLoading) {
                event.preventDefault()
                return
            }

            if (closeOnSelect) {
                const promise: Promise<unknown> = onSelect?.(event) ?? ({} as Promise<unknown>)

                // If this is a promise, wait for it to resolve before closing the dropdown.
                if (typeof promise === 'object' && 'then' in promise) {
                    event.preventDefault()
                    setIsLoadingInternal(true)
                    promise.finally(() => {
                        setIsLoadingInternal(false)
                        closeRoot()
                    })
                }

                return promise
            } else {
                event.preventDefault()
            }
        }

        const slotProps = { disabled: props.disabled || false }

        return (
            <Item onSelect={handleSelect} aria-busy={effectiveIsLoading} {...props} ref={ref}>
                {LeftSlotContent && (
                    <LeftSlot>
                        <LeftSlotContent {...slotProps} />
                    </LeftSlot>
                )}
                {children}
                {(RightSlotContent || effectiveIsLoading) && (
                    <RightSlot>
                        {effectiveIsLoading && <Spinner size="xs" role="presentation" />}
                        {!effectiveIsLoading && RightSlotContent && (
                            <RightSlotContent {...slotProps} />
                        )}
                    </RightSlot>
                )}
            </Item>
        )
    }
)
